/**
 * One statement's fields, with the selected span of years side by side.
 *
 * ── The application's own collapsible ────────────────────────────────────────
 * A `Card` wrapping a `<button aria-expanded>`, with `ChevronDown`/`ChevronUp`
 * and a pill count badge — the same shape as `sections/AssumedInputsPanel`.
 * This was a hand-rolled `<details>` with a text `▸` and a monospace count,
 * which read as a different application. Controlled state also makes open and
 * closed deterministic, where React's handling of the `open` attribute left a
 * panel that was passed `defaultOpen` rendering shut.
 *
 * ── The sticky first column ──────────────────────────────────────────────────
 * Year columns scroll inside their own container while the field name stays
 * pinned. Without it, scrolling right to reach FY2017 loses the only thing
 * identifying the row, and the page itself would scroll sideways.
 *
 * ── Markers, not columns ─────────────────────────────────────────────────────
 * `origin` and `validation` are not columns, because the API review showed they
 * barely vary: `origin` is effectively always `reported`, and `validation` is
 * `not_reviewed` for anything the bounded agent digest never reached. What DOES
 * vary is which individual cells a finding named, and that is per (year, field)
 * — so it is drawn on the cell, and the row's Note names the reason once. Both
 * markers carry a word in their accessible name, never colour alone.
 */

import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Card from '../../ui/Card';
import Tooltip from '../../ui/Tooltip';
import type { FieldCoverageEntry } from '../../../types/dataQuality';
import { formatFiscalYear } from '../utils/dataQualityFormat';
import { VALIDATION_TOKENS } from './dqTokens';
import { formatFieldValue, isFlagged, type PivotRow } from './fieldCoverage';

export interface FieldPivotTableProps {
  label: string;
  rows: PivotRow[];
  /** The visible span, newest first. */
  years: number[];
  /** Total rows before the search narrowed them, for the "N of M" badge. */
  totalRows: number;
  /** The field to ring, when the reader arrived from a finding's chip. */
  highlightField?: string | null;
  /** Whether the panel should be open — a search or a deep link forces it. */
  open?: boolean;
}

/** A zero that stands in for a missing figure — the dangerous one. */
const DEFAULTED_HINT =
  'A zero substituted for a missing value — it can distort any ratio built on it.';

function Cell({ entry }: { entry: FieldCoverageEntry | undefined }) {
  if (!entry) {
    // The year exists for this statement but never held this field. Distinct
    // from a stored null, which is an em-dash.
    return <span className="text-neutral-300">·</span>;
  }

  const { display, exact } = formatFieldValue(entry);
  const flagged = isFlagged(entry);
  const defaulted = entry.origin === 'defaulted';
  const missing = entry.value == null || !Number.isFinite(entry.value);

  const figure = (
    <span
      className={
        missing
          ? 'text-neutral-300'
          : flagged
            ? 'font-semibold text-red-800'
            : 'text-neutral-800'
      }
    >
      {display}
    </span>
  );

  const marker = flagged ? (
    <span
      className={`ml-1 text-[9px] leading-none ${VALIDATION_TOKENS[entry.validation].text}`}
      aria-hidden="true"
    >
      {VALIDATION_TOKENS[entry.validation].glyph}
    </span>
  ) : defaulted ? (
    <span className="ml-1 text-[9px] leading-none text-amber-600" aria-hidden="true">
      ⚑
    </span>
  ) : null;

  // The tooltip carries the exact figure, the state and, for a defaulted zero,
  // why it is dangerous — everything the glyph cannot say on its own.
  const hint = [
    exact,
    flagged ? `${VALIDATION_TOKENS[entry.validation].label}: ${entry.reason ?? '—'}` : null,
    defaulted ? DEFAULTED_HINT : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const body = (
    <span className="inline-flex items-baseline justify-end">
      {figure}
      {marker}
    </span>
  );

  return hint ? (
    <Tooltip content={hint} position="top">
      <span className="cursor-help">{body}</span>
    </Tooltip>
  ) : (
    body
  );
}

export default function FieldPivotTable({
  label,
  rows,
  years,
  totalRows,
  highlightField,
  open = false,
}: FieldPivotTableProps) {
  const [expanded, setExpanded] = useState(open);

  // Follows the caller in BOTH directions: a search opens the panels it
  // matched, and clearing the search closes them again.
  useEffect(() => setExpanded(open), [open]);

  if (rows.length === 0) return null;

  const flagged = rows.filter((row) => row.flagged).length;
  const narrowed = rows.length !== totalRows;

  return (
    <Card>
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((previous) => !previous)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-neutral-50 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-500"
      >
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-neutral-800">{label}</h3>
          <p className="mt-0.5 text-xs text-neutral-500">
            {narrowed
              ? `${rows.length} of ${totalRows} fields match`
              : `${totalRows} field${totalRows === 1 ? '' : 's'}`}
          </p>
        </div>

        <span className="flex shrink-0 items-center gap-2">
          {flagged > 0 && (
            <span className="rounded-full border border-red-100 bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
              {flagged} flagged
            </span>
          )}
          <span className="rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-[11px] font-medium text-neutral-700">
            {rows.length}
          </span>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-neutral-400" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-4 w-4 text-neutral-400" aria-hidden="true" />
          )}
        </span>
      </button>

      {expanded && (
        // The scroll container is the TABLE's, not the page's.
        <div className="overflow-x-auto border-t border-neutral-100">
          <table className="w-full border-collapse text-left text-xs">
            <thead>
              <tr className="border-b border-neutral-200 text-[10px] uppercase tracking-wider text-neutral-400">
                <th
                  scope="col"
                  className="sticky left-0 z-10 whitespace-nowrap bg-white py-2 pl-4 pr-4 font-semibold"
                >
                  Field
                </th>
                {years.map((year) => (
                  <th
                    key={year}
                    scope="col"
                    className="whitespace-nowrap px-3 py-2 text-right font-semibold"
                  >
                    {formatFiscalYear(year)}
                  </th>
                ))}
                <th scope="col" className="whitespace-nowrap py-2 pl-4 pr-4 font-semibold">
                  Note
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const highlighted = !!highlightField && row.canonical === highlightField;

                // Priority: an actual FAILED check outranks a merely
                // defaulted zero, which outranks a bare `uncertain` (no wash
                // — the cell marker alone is enough for an agent abstention).
                // With neither, a faint zebra keeps a 30+ row statement
                // scannable without the eye losing the line.
                const rowTone = row.hasFailed
                  ? 'bg-red-50'
                  : row.defaulted
                    ? 'bg-amber-50/70'
                    : index % 2 === 1
                      ? 'bg-neutral-50/60'
                      : '';
                // The sticky column needs its OWN opaque background — a
                // `position: sticky` cell paints over whatever scrolls
                // beneath it, so it cannot rely on the row's background
                // showing through the way an ordinary `<td>` can.
                const stickyBg = row.hasFailed
                  ? 'bg-red-50'
                  : row.defaulted
                    ? 'bg-amber-50'
                    : index % 2 === 1
                      ? 'bg-neutral-50'
                      : 'bg-white';

                return (
                  <tr
                    key={row.canonical}
                    // The anchor a finding's field chip links to.
                    id={`dq-field-${row.canonical}`}
                    className={`border-b border-neutral-100 last:border-b-0 ${rowTone} ${
                      highlighted ? 'ring-2 ring-inset ring-primary-400' : ''
                    }`}
                  >
                    {/* Verbatim, monospace, and pinned — the reader matches
                        this against their own graph while scrolling years. */}
                    <th
                      scope="row"
                      className={`sticky left-0 z-10 whitespace-nowrap py-1.5 pl-4 pr-4 text-left font-mono text-[11px] font-normal text-neutral-800 ${stickyBg}`}
                    >
                      {row.field}
                    </th>

                    {years.map((year) => (
                      <td
                        key={year}
                        className="whitespace-nowrap px-3 py-1.5 text-right font-mono tabular-nums"
                      >
                        <Cell entry={row.cells.get(year)} />
                      </td>
                    ))}

                    {/* An icon, not a repeated sentence: the same note was
                        showing up to a dozen times per statement, each taking
                        a full-width line. The word survives in the legend
                        below the table and in this cell's own tooltip /
                        `aria-label` — the icon plus the row's own tone is what
                        stays inline. */}
                    <td className="whitespace-nowrap px-4 py-1.5 text-center">
                      {row.note ? (
                        <Tooltip content={row.note} position="top">
                          <span
                            role="img"
                            aria-label={row.note}
                            className="cursor-help text-red-600"
                          >
                            ⚠
                          </span>
                        </Tooltip>
                      ) : row.defaulted ? (
                        <Tooltip content={DEFAULTED_HINT} position="top">
                          <span
                            role="img"
                            aria-label="Zero standing in for a missing value"
                            className="cursor-help text-amber-600"
                          >
                            ⚑
                          </span>
                        </Tooltip>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
